/**
 * CountryCodePicker — flag + dial code chip → searchable country sheet.
 *
 * Apple-level bar for this control:
 * - Tapping the chip NEVER opens the keyboard.
 * - The sheet shows every supported country fully, not half a row.
 * - Search is opt-in (tap the field); only then may the keyboard appear,
 *   and the list still has room above it.
 * - Keyboard from the phone field is dismissed before the sheet opens.
 */
import { useCallback, useMemo, useState } from 'react'
import {
  View,
  Text,
  TextInput as RNTextInput,
  Platform,
  Keyboard,
  ScrollView,
  useWindowDimensions,
} from 'react-native'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ChevronDown, Search, X, Check } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { TrackedNativePressable as RNPressable } from './TrackedNativePressable'
import { CountryFlag } from './CountryFlag'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import {
  DEFAULT_COUNTRY_ISO2,
  findCountry,
  supportedPhoneCountries,
  type Country,
} from '../../lib/data/countries'

/** Row height — at least 44dp touch target, room for flag + two labels. */
const ROW_HEIGHT = 52

interface CountryCodePickerProps {
  value: string
  onChange: (iso2: string) => void
  error?: boolean
  testID?: string
  countries?: readonly Country[]
}

interface CountryRowProps {
  country: Country
  selected: boolean
  onPress: (iso2: string) => void
  locale: 'es' | 'en'
}

function CountryRow({ country, selected, onPress, locale }: CountryRowProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <RNPressable
      onPress={() => onPress(country.iso2)}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: ROW_HEIGHT,
        paddingHorizontal: SPACING.lg,
        paddingVertical: SPACING.sm,
        backgroundColor: selected
          ? withAlphaPrimary(colors.primary, isDark)
          : pressed
            ? colors.surfaceSubtle
            : 'transparent',
        gap: SPACING.md,
      })}
      accessibilityRole="button"
      accessibilityLabel={`${locale === 'es' ? country.nameEs : country.nameEn} +${country.dialCode}`}
      accessibilityState={{ selected }}
      testID={`phone.country.row.${country.iso2}`}
    >
      <CountryFlag iso2={country.iso2} width={28} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 16,
            lineHeight: 21,
            fontFamily: selected ? FONTS.bodyBold : FONTS.body,
            color: colors.textPrimary,
          }}
          numberOfLines={1}
        >
          {locale === 'es' ? country.nameEs : country.nameEn}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 15,
          color: colors.textSecondary,
          fontVariant: ['tabular-nums'],
        }}
      >
        +{country.dialCode}
      </Text>
      {selected ? (
        <Check size={18} color={colors.primary} strokeWidth={2.4} />
      ) : (
        <View style={{ width: 18 }} />
      )}
    </RNPressable>
  )
}

function withAlphaPrimary(primary: string, isDark: boolean): string {
  // Soft selected wash — never a solid slab.
  if (primary.startsWith('#') && primary.length === 7) {
    return isDark ? `${primary}22` : `${primary}14`
  }
  return isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
}

export function CountryCodePicker({
  value,
  onChange,
  error,
  testID,
  countries,
}: CountryCodePickerProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { height: windowHeight } = useWindowDimensions()
  const locale: 'es' | 'en' = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es'

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  const supportedCountries = useMemo(() => countries ?? supportedPhoneCountries(), [countries])
  const selected =
    supportedCountries.find((country) => country.iso2 === value) ??
    findCountry(value) ??
    supportedCountries[0] ??
    findCountry(DEFAULT_COUNTRY_ISO2)!
  const isLocked = supportedCountries.length <= 1

  // Prefer product order: home first, then the markets people actually use.
  const sortedCountries = useMemo(() => {
    const priority = ['SV', 'US', 'GT', 'HN', 'NI', 'CR', 'PA', 'BZ', 'MX', 'CA']
    const rank = (iso2: string) => {
      const i = priority.indexOf(iso2)
      return i === -1 ? priority.length + 1 : i
    }
    return [...supportedCountries].sort((a, b) => {
      const byPriority = rank(a.iso2) - rank(b.iso2)
      if (byPriority !== 0) return byPriority
      return (locale === 'es' ? a.nameEs : a.nameEn).localeCompare(
        locale === 'es' ? b.nameEs : b.nameEn,
      )
    })
  }, [locale, supportedCountries])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sortedCountries
    return sortedCountries.filter((c) => {
      const name = (locale === 'es' ? c.nameEs : c.nameEn).toLowerCase()
      return (
        name.includes(q) ||
        c.dialCode.includes(q.replace(/^\+/, '')) ||
        c.iso2.toLowerCase().includes(q)
      )
    })
  }, [sortedCountries, query, locale])

  // Full list visible without scroll when few countries; otherwise scroll.
  // Shrink when search keyboard is open so the list never hides under it.
  const idealListHeight = filtered.length * ROW_HEIGHT + SPACING.sm
  const maxListHeight = searchFocused
    ? Math.round(windowHeight * 0.28)
    : Math.round(windowHeight * 0.5)
  const listHeight = Math.max(ROW_HEIGHT * 3, Math.min(idealListHeight, maxListHeight, 520))

  const handleOpen = useCallback(() => {
    if (isLocked) return
    // Kill the phone-field keyboard first — that was the "sheet under keyboard" bug.
    Keyboard.dismiss()
    setQuery('')
    setSearchFocused(false)
    // Next frame so Android finishes dismissing the IME before the sheet mounts.
    requestAnimationFrame(() => setOpen(true))
  }, [isLocked])

  const handleClose = useCallback(() => {
    Keyboard.dismiss()
    setSearchFocused(false)
    setQuery('')
    setOpen(false)
  }, [])

  const handleSelect = useCallback(
    (iso2: string) => {
      onChange(iso2)
      Keyboard.dismiss()
      setSearchFocused(false)
      setQuery('')
      setOpen(false)
    },
    [onChange],
  )

  return (
    <>
      <Pressable
        onPress={handleOpen}
        disabled={isLocked}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={t('phone.country_picker_label', {
          country: locale === 'es' ? selected.nameEs : selected.nameEn,
        })}
        accessibilityHint={isLocked ? undefined : t('phone.country_picker_hint')}
        accessibilityState={{ disabled: isLocked }}
        // Prevent the neighbouring phone TextInput from stealing the tap.
        hitSlop={{ top: 4, bottom: 4, left: 4, right: 0 }}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs + 2,
          paddingHorizontal: SPACING.md,
          minHeight: INTERACTION.minTouchTarget,
          borderRightWidth: 1,
          borderRightColor: error ? COLORS.coral.DEFAULT : colors.border,
          opacity: pressed && !isLocked ? 0.72 : 1,
        })}
      >
        <CountryFlag iso2={selected.iso2} width={26} />
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            color: colors.textPrimary,
            fontVariant: ['tabular-nums'],
          }}
        >
          +{selected.dialCode}
        </Text>
        {isLocked ? null : <ChevronDown size={14} color={colors.textSecondary} strokeWidth={2} />}
      </Pressable>

      <AppModal
        visible={!isLocked && open}
        animationType="slide"
        transparent
        onRequestClose={handleClose}
        presentationStyle="overFullScreen"
        statusBarTranslucent
      >
        <View
          style={{
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
          <RNPressable
            style={{ flex: 1 }}
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
          />

          <GlassSheet
            style={{
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
            }}
            contentStyle={{
              paddingTop: SPACING.md,
              paddingBottom: Math.max(insets.bottom, SPACING.lg),
            }}
            testID={testID ? `${testID}.sheet` : 'phone.country.sheet'}
          >
            {/* Drag handle */}
            <View
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                backgroundColor: colors.border,
                alignSelf: 'center',
                marginBottom: SPACING.md,
              }}
            />

            {/* Header */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: SPACING.lg,
                marginBottom: SPACING.md,
              }}
            >
              <Text
                style={{
                  flex: 1,
                  fontSize: 18,
                  lineHeight: 24,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  letterSpacing: -0.2,
                }}
              >
                {t('phone.country_picker_title')}
              </Text>
              <Pressable
                onPress={handleClose}
                hitSlop={12}
                accessibilityRole="button"
                accessibilityLabel={t('common.close')}
                style={{
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={22} color={colors.textSecondary} />
              </Pressable>
            </View>

            {/* Search — NOT auto-focused. Tap only if the user wants to filter. */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginHorizontal: SPACING.lg,
                marginBottom: SPACING.sm,
                paddingHorizontal: SPACING.md,
                minHeight: INTERACTION.minTouchTarget,
                borderRadius: RADIUS.md,
                borderWidth: 1,
                borderColor: searchFocused
                  ? isDark
                    ? COLORS.coral.DEFAULT
                    : COLORS.purple.light
                  : colors.border,
                backgroundColor: colors.surfaceSubtle,
                gap: SPACING.sm,
              }}
            >
              <Search size={18} color={colors.textSecondary} strokeWidth={2} />
              <RNTextInput
                value={query}
                onChangeText={setQuery}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={t('phone.country_picker_search')}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                // Critical: do not auto-focus. Opening the sheet must not
                // raise the IME and crush the country list.
                autoFocus={false}
                returnKeyType="search"
                blurOnSubmit
                style={[
                  {
                    flex: 1,
                    fontSize: 16,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                    paddingVertical: SPACING.sm,
                  },
                  Platform.OS === 'web'
                    ? ({ outlineStyle: 'none' } as Record<string, string>)
                    : null,
                ]}
                testID={testID ? `${testID}.search` : 'phone.country.search'}
              />
              {query.length > 0 ? (
                <Pressable
                  onPress={() => setQuery('')}
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.clear')}
                >
                  <X size={16} color={colors.textSecondary} />
                </Pressable>
              ) : null}
            </View>

            {/* Full country list — ScrollView is the right tool for ~10 rows. */}
            <ScrollView
              style={{ height: listHeight }}
              contentContainerStyle={{ paddingBottom: SPACING.sm }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              showsVerticalScrollIndicator={filtered.length > 8}
              testID={testID ? `${testID}.list` : 'phone.country.list'}
            >
              {filtered.length === 0 ? (
                <View
                  style={{
                    paddingHorizontal: SPACING.lg,
                    paddingVertical: SPACING.xl,
                    alignItems: 'center',
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 15,
                      color: colors.textSecondary,
                      textAlign: 'center',
                    }}
                  >
                    {t('phone.country_picker_empty')}
                  </Text>
                </View>
              ) : (
                filtered.map((item) => (
                  <CountryRow
                    key={item.iso2}
                    country={item}
                    selected={item.iso2 === selected.iso2}
                    onPress={handleSelect}
                    locale={locale}
                  />
                ))
              )}
            </ScrollView>
          </GlassSheet>
        </View>
      </AppModal>
    </>
  )
}
